import React, { useEffect, useState, useRef } from "react";
import {
  ColumnDef,
  ExpandedState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import styles from "./Table.module.css";

export type TableRowData = {
  id: string;
  type?: "project" | "group";
  /** Optional children (for hierarchical rows) */
  subRows?: TableRowData[];
  [key: string]: any;
};

type PersistedTableState = {
  sorting?: SortingState;
  expanded?: ExpandedState;
};

type Props<TData extends TableRowData> = {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  /** Row click (project or group) */
  onRowClick?: (row: TData) => void;
  /** Optional selected row id, used only for styling */
  selectedRowId?: string | null;
  /** Optional hook to render a per-row class */
  getRowClassName?: (row: TData) => string | undefined;
  /** Optional extra class for the <table> element (for table-specific layout tweaks) */
  tableClassName?: string;
  /** Optional persistence key: when provided, sorting/expanded state are stored in localStorage */
  persistKey?: string;
  /**
   * When set, the table will auto-expand ancestor groups containing this row
   * and scroll the row into view. Use a project_guid or row id.
   */
  focusRowId?: string | null;
};

/* ------------------------------------------------------------------ */
/*  Walk the data tree to find ancestor group ids for a target row     */
/* ------------------------------------------------------------------ */

function findAncestorIds<T extends TableRowData>(
  rows: T[],
  targetId: string,
  path: string[] = [],
): string[] | null {
  for (const row of rows) {
    const rid = String(row?.id ?? "");
    // Also check project_guid for project rows
    const pguid = String(
      (row as any)?.project_guid ??
        (row as any)?.project?.project_guid ??
        (row as any)?.project?.project_GUID ??
        "",
    );
    if (rid === targetId || (pguid && pguid === targetId)) {
      return path; // found — return ancestor path (not including self)
    }
    if (Array.isArray(row.subRows) && row.subRows.length > 0) {
      const result = findAncestorIds(row.subRows as T[], targetId, [...path, rid]);
      if (result) return result;
    }
  }
  return null; // not found in this branch
}

const safeJsonParse = <T,>(raw: string | null, fallback: T): T => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

/**
 * TanStack Table v8 wrapper.
 *
 * Note: grouping is handled upstream by shaping `data` into a hierarchy using `subRows`.
 * This keeps indentation, sorting, and expand/collapse behavior under our control.
 */
export default function Table<TData extends TableRowData>({
  columns,
  data,
  onRowClick,
  selectedRowId,
  getRowClassName,
  tableClassName,
  persistKey,
  focusRowId,
}: Props<TData>) {
  const storageKey = persistKey ? `l4e_table_state::${persistKey}` : null;

  const loadPersisted = (): PersistedTableState => {
    if (!storageKey) return {};
    try {
      return safeJsonParse<PersistedTableState>(window?.localStorage?.getItem(storageKey) ?? null, {});
    } catch {
      return {};
    }
  };

  const persisted = useRef<PersistedTableState>(loadPersisted());

  const [sorting, setSorting] = useState<SortingState>(() => persisted.current.sorting ?? []);
  const [expanded, setExpanded] = useState<ExpandedState>(() => persisted.current.expanded ?? {});

  // Tracks the "view" so we only auto-expand on view changes (load / collection / group-by),
  // and we do NOT fight the user's manual collapse/expand clicks.
  const autoExpandSignatureRef = useRef<string>("");

  // When the persistence key changes (new view), reload persisted state.
  useEffect(() => {
    if (!storageKey) return;
    const st = loadPersisted();
    persisted.current = st;
    setSorting(st.sorting ?? []);
    setExpanded(st.expanded ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Persist state on change.
  useEffect(() => {
    if (!storageKey) return;
    try {
      const next: PersistedTableState = { sorting, expanded };
      window?.localStorage?.setItem(storageKey, JSON.stringify(next));
    } catch {
      // ignore
    }
  }, [storageKey, sorting, expanded]);

  // Auto-expand the first level (root groups) only when the dataset shape changes.
  useEffect(() => {
    if (!Array.isArray(data) || data.length === 0) return;

    // Collect expandable top-level row ids in the *current* dataset.
    const topIds: string[] = [];
    for (let i = 0; i < data.length; i++) {
      const row: any = data[i];
      const id = String(row?.id ?? "");
      const subRows = row?.subRows;
      if (id && Array.isArray(subRows) && subRows.length > 0) topIds.push(id);
    }
    if (topIds.length === 0) return;

    // Signature changes when the view changes (collection/group-by/filter changes will reshape data).
    const signature = `${topIds.join("|")}::len=${data.length}`;

    if (autoExpandSignatureRef.current !== signature) {
      autoExpandSignatureRef.current = signature;

      // Expand all top-level rows for this view (e.g., expand "Overall Health" so the next level shows).
      const next: ExpandedState = {};
      for (let i = 0; i < topIds.length; i++) next[topIds[i]] = true;
      setExpanded(next);
    }
  }, [data]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting, expanded },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,

    // IMPORTANT: Use the row's stable `id` field as TanStack's internal row id.
    // Otherwise TanStack uses positional indices (e.g., "0", "0.0"), and our
    // expansion state keyed by `row.id` won't match.
    getRowId: (row) => String((row as any)?.id ?? ""),

    getSubRows: (row) => (row as any).subRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),

    // Keep expanded state stable when data changes (e.g., filtering)
    autoResetExpanded: false,
    enableSortingRemoval: false,
  });

  /* ---------------------------------------------------------------- */
  /*  Focus row: expand ancestors + scroll into view                    */
  /* ---------------------------------------------------------------- */

  const tbodyRef = useRef<HTMLTableSectionElement | null>(null);
  const lastFocusedRef = useRef<string>("");

  useEffect(() => {
    const fid = String(focusRowId || "").trim();
    if (!fid || fid === lastFocusedRef.current) return;
    lastFocusedRef.current = fid;

    // 1. Find ancestors in the data tree and expand them
    const ancestors = findAncestorIds(data as unknown as TableRowData[], fid);
    if (ancestors && ancestors.length > 0) {
      setExpanded((prev) => {
        const next = typeof prev === "object" ? { ...prev } : {};
        let changed = false;
        for (const aid of ancestors) {
          if (!(next as Record<string, boolean>)[aid]) {
            (next as Record<string, boolean>)[aid] = true;
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }

    // 2. After React re-renders with expanded groups, scroll the row into view
    const scrollTimer = window.setTimeout(() => {
      const tbody = tbodyRef.current;
      if (!tbody) return;
      // Find the <tr> whose data-row-id matches (we add this attribute below)
      const targetTr = tbody.querySelector(`[data-row-id="${CSS.escape(fid)}"]`);
      if (targetTr) {
        targetTr.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 80); // small delay to let expansion render

    return () => window.clearTimeout(scrollTimer);
  }, [focusRowId, data]);

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;

  return (
    <div>
      <table
        className={[styles.table, tableClassName ?? ""]
          .filter(Boolean)
          .join(" ")}
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr key={headerGroup.id} className={styles["header-row"]}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                return (
                  <th
                    key={header.id}
                    className={styles["header-cell"]}
                    onClick={
                      canSort
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                    style={{ cursor: canSort ? "pointer" : undefined }}
                  >
                    <div className={styles["header-cell-content"]}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {canSort ? (
                        <span
                          style={{ marginInlineStart: 6, opacity: 0.8, fontSize: 12 }}
                        >
                          {header.column.getIsSorted() === "asc"
                            ? "▲"
                            : header.column.getIsSorted() === "desc"
                            ? "▼"
                            : ""}
                        </span>
                      ) : null}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody ref={tbodyRef}>
          {rows.map((row) => {
            const original = row.original as unknown as TData;
            const isSelected = selectedRowId && original?.id === selectedRowId;
            const customClass = getRowClassName?.(original);
            // data-row-id: use project_guid (for project rows) or row.id (for groups)
            const rowDataId = String(
              (original as any)?.project_guid ??
                (original as any)?.project?.project_guid ??
                (original as any)?.project?.project_GUID ??
                original?.id ??
                row.id,
            );
            return (
              <tr
                key={row.id}
                data-row-id={rowDataId}
                className={[
                  styles["table-row"],
                  "interactive",
                  isSelected ? styles["active-row"] : "",
                  customClass ?? "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => {
                  // Row click should NOT toggle expand/collapse; we do that explicitly in the expander control.
                  onRowClick?.(original);
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={styles["table-cell"]}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
